// A backend that lives in the page. Static previews (no API at this origin)
// use it so the room still opens: the starter shelf, and base skins.

import { CUSTOM_SKIN, DEVICES, MEMBERSHIP, ROOMS, DEFAULT_ROOM, parseColor, type AppCatalog } from "../core/catalog.ts";
import { PaymentRequired, Refused, type StudioBackend } from "../core/studio.ts";
import type { AppInfo, DeviceId, StudioSnapshot } from "../core/types.ts";

/**
 * `member` previews the room as a member sees it. This backend stands in
 * only where no API answers, so it gates nothing real.
 */
export async function localBackend(appsUrl: string, member = false, extra = 0): Promise<StudioBackend> {
  let catalog: AppCatalog = { apps: [] };
  try {
    const response = await fetch(appsUrl + "apps.json");
    if (response.ok) catalog = await response.json();
  } catch { /* an empty shelf is still a room */ }

  const skins = new Map<DeviceId, string>(DEVICES.map((device) => [device.id, device.skins[0]!.id]));
  const colors = new Map<DeviceId, string>();
  let room = DEFAULT_ROOM;
  const mine: AppInfo[] = [];
  // `extra` games other people published, for looking at a rack of several pages. Each plays the build of a starter.
  const PAIRS = [["#3fd0e8", "#ff5f9e"], ["#ffd23f", "#a98bff"], ["#ffb45c", "#3fd0e8"], ["#a98bff", "#ffd23f"], ["#ff5f9e", "#ffb45c"]] as const;
  const others = (): AppInfo[] => Array.from({ length: Math.min(extra, 200) }, (_, index) => {
    const like = starters[index % Math.max(1, starters.length)];
    const number = String(index + 1).padStart(2, "0");
    return {
      ...like!,
      id: `shared-${number}`,
      title: `Game ${number}`,
      author: "someone",
      tagline: `A game somebody published, number ${index + 1}.`,
      origin: "shared",
      shot: null,
      colors: [...PAIRS[index % PAIRS.length]!],
    };
  });
  let rev = 1;

  const starters: AppInfo[] = catalog.apps.map((app) => ({
    kind: "pocket",
    slug: null,
    url: null,
    verified: true,
    packages: [],
    site: null,
    id: app.id,
    title: app.title,
    author: app.author,
    tagline: app.tagline,
    origin: "starter",
    status: "published",
    devices: Object.keys(app.builds) as DeviceId[],
    builds: Object.fromEntries(Object.entries(app.builds).map(([device, build]) => [device, { js: appsUrl + build!.js, pak: appsUrl + build!.pak }])),
    shot: appsUrl + app.shot,
    colors: app.colors,
    updatedAt: 0,
  }));

  const snapshot = (): StudioSnapshot => ({
    user: { id: "local", handle: "guest", name: "Guest", avatar: null, guest: true },
    membership: { active: member, price: MEMBERSHIP.price, period: MEMBERSHIP.period },
    devices: DEVICES.map((device) => ({
      id: device.id,
      name: device.name,
      viewport: device.viewport,
      auxiliary: device.auxiliary,
      input: device.input,
      skin: skins.get(device.id)!,
      skins: device.skins.map((skin) => ({ ...skin, owned: skin.tier === "base" || member })),
      color: colors.get(device.id) ?? null,
    })),
    apps: [...mine, ...starters, ...(starters.length > 0 ? others() : [])],
    rooms: ROOMS.map((entry) => ({ id: entry.id, title: entry.title, tier: entry.tier, owned: entry.tier === "base" || member, active: entry.id === room })),
    rev,
  });

  return {
    snapshot: async () => snapshot(),
    async setSkin(device, skin, color) {
      const due = () => new PaymentRequired("membership", "membership", MEMBERSHIP.price);
      if (skin === CUSTOM_SKIN) {
        const own = parseColor(color);
        if (!own) throw new Error("a custom colour is #rrggbb");
        if (!member) throw due();
        colors.set(device, own);
      } else {
        const entry = DEVICES.find((d) => d.id === device)?.skins.find((s) => s.id === skin);
        if (!entry) throw new Error(`unknown skin ${skin}`);
        if (entry.tier !== "base" && !member) throw due();
      }
      skins.set(device, skin);
      rev++;
      return snapshot();
    },
    async setRoom(next) {
      const entry = ROOMS.find((r) => r.id === next);
      if (!entry) throw new Error(`unknown room ${next}`);
      if (!entry.available) throw new Refused("More rooms are coming soon.");
      if (entry.tier !== "base" && !member) throw new PaymentRequired("membership", "membership", MEMBERSHIP.price);
      room = next;
      rev++;
      return snapshot();
    },
    // The offline preview has no other authors' games to keep.
    async setKept() {
      return snapshot();
    },
    async setTitle(app, title) {
      const entry = mine.find((candidate) => candidate.id === app);
      if (entry) entry.title = title;
      rev++;
      return snapshot();
    },
    async setPublished(app, on) {
      const entry = mine.find((candidate) => candidate.id === app);
      if (entry) entry.status = on ? "published" : "draft";
      rev++;
      return snapshot();
    },
    async setNativeAllowed(app, on) {
      const entry = mine.find((candidate) => candidate.id === app);
      if (entry) entry.allowNative = on;
      rev++;
      return snapshot();
    },
    // This preview stores no packages.
    async nativeDownload() {
      throw new Refused("The author has not uploaded an installation package for this device yet");
    },
    // An address is a host of the server, and this preview has none.
    async setSlug() {
      throw new Refused("This preview has no server, so a game has no address here.");
    },
    async deleteApp(app) {
      const at = mine.findIndex((candidate) => candidate.id === app);
      if (at >= 0) mine.splice(at, 1);
      rev++;
      return snapshot();
    },
    // A handle belongs to an account, and this preview has none.
    async setHandle() {
      throw new Refused("This preview has no server, so there is no account to name.");
    },
    async signOut() {},
  };
}
